import { useRef, useEffect, useState, useCallback } from "react";

export enum ConnectionSpeed {
  VERY_LOW = "Very Low",
  LOW = "Low",
  MEDIUM = "Medium",
  HIGH = "High",
}

const imageAddr =
  "https://vertelier.com/do_not_delete_internet_speed_checker.png";
const downloadSize = 311000;
const TEST_COUNT = 3;

export const useMeasureConnectionSpeed = () => {
  const isFirstRender = useRef(true);
  const [speedKbps, setSpeedKbps] = useState<number | null>(null);
  const [speedCategory, setSpeedCategory] = useState<ConnectionSpeed | null>(
    null
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      initiateSpeedDetection();
    }
  }, []);

  const initiateSpeedDetection = useCallback(() => {
    runSpeedTests(TEST_COUNT).then((averageSpeed) => {
      setSpeedKbps(averageSpeed);
      setSpeedCategory(determineSpeedCategory(averageSpeed));
    });
  }, []);

  const runSpeedTests = async (count: number): Promise<number> => {
    const results: number[] = [];
    for (let i = 0; i < count; i++) {
      try {
        const speed = await measureSingleTest();
        results.push(speed);
      } catch (e) {
        results.push(0); // fallback on error
      }
    }
    const average = results.reduce((sum, val) => sum + val, 0) / results.length;
    return parseFloat(average.toFixed(2));
  };

  const measureSingleTest = (): Promise<number> => {
    return new Promise((resolve, reject) => {
      const download = new Image();
      const startTime = performance.now();

      const timeout = setTimeout(() => {
        download.src = ""; // Cancel download
        reject("Timeout");
      }, 10000); // 10 seconds

      download.onload = function () {
        clearTimeout(timeout);
        const endTime = performance.now();
        const duration = (endTime - startTime) / 1000; // seconds
        const bitsLoaded = downloadSize * 8;
        const speedBps = bitsLoaded / duration;
        const speedKbps = speedBps / 1024;
        resolve(speedKbps);
      };

      download.onerror = function () {
        clearTimeout(timeout);
        reject("Error downloading image.");
      };

      const cacheBuster = `?nnn=${startTime}`;
      download.src = imageAddr + cacheBuster;
    });
  };

  const determineSpeedCategory = (speed: number): ConnectionSpeed => {
    if (speed < 1000) {
      return ConnectionSpeed.VERY_LOW;
    } else if (speed >= 1000 && speed < 1300) {
      return ConnectionSpeed.LOW;
    } else if (speed >= 1300 && speed < 2100) {
      return ConnectionSpeed.MEDIUM;
    } else {
      return ConnectionSpeed.HIGH;
    }
  };

  return { speedKbps, speedCategory, refresh: initiateSpeedDetection };
};
